import React from "react";

/* ─────────── STAT CARD ─────────── */
export const StatCard = ({ icon: Icon, label, value, color = "text-white", accent = "bg-white/[0.04]", testid }) => (
  <div
    data-testid={testid}
    className="bg-[#141414] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4 hover:border-white/[0.12] transition-colors"
  >
    <div className={`w-10 h-10 rounded-lg ${accent} flex items-center justify-center shrink-0`}>
      <Icon size={18} className={color} />
    </div>
    <div className="min-w-0">
      <p className="text-white/40 text-[11px] uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold ${color} truncate`}>{value}</p>
    </div>
  </div>
);

/* ─────────── STATUS BADGE ─────────── */
export const StatusBadge = ({ config, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border ${config.color}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
    {label || config.label}
  </span>
);

/* ─────────── FILTER PILL ─────────── */
export const FilterPill = ({ active, onClick, children, accent = "bg-[#FF3B30]" }) => (
  <button
    onClick={onClick}
    className={`px-3.5 py-1.5 text-xs rounded-md font-medium transition-all border ${
      active
        ? `${accent} text-white border-transparent shadow-lg shadow-red-500/20`
        : "bg-[#1C1C1E] text-white/60 border-white/[0.06] hover:text-white hover:border-white/[0.12]"
    }`}
  >
    {children}
  </button>
);

/* ─────────── INFO ROW (vertical data) ─────────── */
export const InfoRow = ({ icon: Icon, label, value, accent = "bg-white/[0.04]", valueClass = "text-white" }) => (
  <div className="flex items-start gap-3 py-1.5">
    <div className={`w-7 h-7 rounded-md ${accent} flex items-center justify-center shrink-0 mt-0.5`}>
      <Icon size={13} className="text-white/50" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white/35 text-[10px] uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-medium truncate ${valueClass}`}>{value || "—"}</p>
    </div>
  </div>
);

/* ─────────── SECTION CARD ─────────── */
export const SectionCard = ({ title, icon: Icon, iconColor = "text-[#FF3B30]", action, children, className = "" }) => (
  <div className={`bg-[#141414] border border-white/[0.06] rounded-xl overflow-hidden ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
        <h3 className="text-white font-semibold flex items-center gap-2.5 text-[15px]">
          {Icon && <Icon size={17} className={iconColor} />}
          {title}
        </h3>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

/* ─────────── ACTION BUTTON (icon + label + color) ─────────── */
export const ActionButton = ({ icon: Icon, label, onClick, color = "white", disabled, testid, title }) => {
  const palette = {
    red: "bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/20",
    green: "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/20",
    blue: "bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border-blue-500/20",
    yellow: "bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-amber-500/20",
    cyan: "bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border-cyan-500/20",
    purple: "bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border-purple-500/20",
    white: "bg-white/[0.04] hover:bg-white/[0.08] text-white/80 border-white/10",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title || label}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium border transition-all disabled:opacity-50 ${palette[color]}`}
    >
      {Icon && <Icon size={14} />}
      {label && <span className="hidden sm:inline">{label}</span>}
    </button>
  );
};

/* ─────────── EMPTY STATE ─────────── */
export const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    {Icon && (
      <div className="w-14 h-14 rounded-xl bg-white/[0.04] flex items-center justify-center mb-4">
        <Icon size={22} className="text-white/30" />
      </div>
    )}
    <p className="text-white/70 font-medium">{title}</p>
    {description && <p className="text-white/40 text-sm mt-1">{description}</p>}
  </div>
);

/* ─────────── SEARCH INPUT ─────────── */
export const SearchInput = ({ value, onChange, placeholder, icon: Icon }) => (
  <div className="relative flex-1 min-w-[200px]">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />}
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md ${Icon ? "pl-10" : "pl-3"} pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20`}
    />
  </div>
);

/* ─────────── STATUS CONFIGS ─────────── */
export const APPOINTMENT_STATUS = {
  request: { label: "À planifier", color: "bg-purple-500/15 text-purple-400 border-purple-500/25", dot: "bg-purple-400" },
  pending: { label: "En attente", color: "bg-amber-500/15 text-amber-400 border-amber-500/25", dot: "bg-amber-400" },
  confirmed: { label: "Confirmé", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", dot: "bg-emerald-400" },
  cancelled: { label: "Annulé", color: "bg-red-500/15 text-red-400 border-red-500/25", dot: "bg-red-400" },
  completed: { label: "Terminé", color: "bg-blue-500/15 text-blue-400 border-blue-500/25", dot: "bg-blue-400" },
};

export const ATELIER_STATUS = {
  reception: { label: "Réception", color: "bg-blue-500/15 text-blue-400 border-blue-500/25", dot: "bg-blue-400" },
  in_progress: { label: "En cours", color: "bg-amber-500/15 text-amber-400 border-amber-500/25", dot: "bg-amber-400" },
  completed: { label: "Terminé", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", dot: "bg-emerald-400" },
};
